import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { generatePath, useNavigate } from 'react-router-dom';

import { ShareModal } from './ShareModal';

import type {
  ShareContextValue,
  ShareHandoverDraft,
  ShareTarget,
} from './Share.types';
import type { ShareProviderProps } from './ShareProvider.types';

import { PLANS_ROUTES } from '@/utils';

const ShareContext = createContext<ShareContextValue | null>(null);

/**
 * ShareProvider - owns the share modal for the authenticated app.
 *
 * Mounted once inside the authenticated layout (under the router + toast
 * contexts). Any descendant page opens the modal imperatively via
 * `useShare().openShare(target)`. Renders a single ShareModal instance.
 */
export function ShareProvider({
  children,
}: ShareProviderProps): React.JSX.Element {
  const navigate = useNavigate();
  const [target, setTarget] = useState<ShareTarget | null>(null);

  const openShare = useCallback((next: ShareTarget) => {
    setTarget(next);
  }, []);

  const closeShare = useCallback(() => {
    setTarget(null);
  }, []);

  const handleSchedulePlan = useCallback(
    (plan: ShareTarget, draft: ShareHandoverDraft) => {
      setTarget(null);
      // Recipients and note travel in router state, not the URL: usernames are
      // personal data and do not belong in a location bar or a history entry.
      // The share page is a superset of this sheet, so anything already typed
      // here must arrive there — otherwise reaching for the schedule silently
      // throws the rest of the form away.
      navigate(generatePath(PLANS_ROUTES.SHARE, { id: plan.id }), {
        state: draft,
      });
    },
    [navigate]
  );

  const value = useMemo<ShareContextValue>(() => ({ openShare }), [openShare]);

  return (
    <ShareContext.Provider value={value}>
      {children}
      <ShareModal
        target={target}
        onClose={closeShare}
        onSchedulePlan={handleSchedulePlan}
      />
    </ShareContext.Provider>
  );
}

/**
 * Access the share API. Must be called within a `ShareProvider`.
 */
export function useShare(): ShareContextValue {
  const context = useContext(ShareContext);
  if (!context) {
    throw new Error('useShare must be used within a ShareProvider');
  }
  return context;
}

export default ShareProvider;
